import { memo, useEffect, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import type { GameState } from '../../../engine';
import { IDLE_TURN_MS } from '../../../worker/protocol';
import type { Emote } from '../../../worker/protocol';
import { LEVEL_LABEL, fmt, tileName } from '../../brand';
import type { SeatInfo } from '../../game/types';
import { faceDown, faceUp, pairedColumns, totalOf } from '../../game/viewModel';
import { PlayerMedallion } from '../Art';
import { MiniCell } from '../Card';
import { Flag, IconOffline } from '../Icons';
import { Sticker } from './Stickers';

export interface TileExtras {
  lastShot: 'owed' | 'done' | null;
  /** Seconds left on this player's idle-turn clock (online), once under the warning window. */
  clock: number | null;
  /** Local time the idle clock runs out (drives the draining ring). */
  deadline: number | null;
  sticker: { id: number; emote: Emote } | null;
  /** Slots that changed in the last move ("p:slot" keys), for the ivory flash. */
  flash: Set<number>;
  /** Columns whose pair just formed → ms before the one-time shimmer. */
  forming?: Map<number, number>;
}

export const OpponentTile = memo(function OpponentTile({
  seat,
  state,
  info,
  compact,
  narrow = false,
  strip = false,
  medal,
  extras,
}: {
  seat: number;
  state: GameState;
  info: SeatInfo;
  compact: boolean;
  /** The tile is too narrow for long names: print the first word. */
  narrow?: boolean;
  /** Phones with 4 or 5 opponents: a slim row (medallion over total, then the mini grid); the name shows on tap. */
  strip?: boolean;
  /** Medallion size override (phones: 20px). */
  medal?: number;
  extras: TileExtras;
}) {
  const [tip, setTip] = useState(false);
  const tipTimer = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => () => clearTimeout(tipTimer.current), []);
  const showTip = () => {
    setTip(true);
    clearTimeout(tipTimer.current);
    tipTimer.current = setTimeout(() => setTip(false), 2000);
  };
  const p = state.players[seat]!;
  const sittingOut = !state.active.includes(seat);
  const active = !sittingOut && (state.phase === 'playing' || state.phase === 'finalTurns') && state.current === seat;
  const teeing = !sittingOut && state.phase === 'initialFlip' && faceUp(p) < 2;
  const out = state.wentOutBy === seat && state.phase === 'finalTurns';
  const pairs = pairedColumns(p.grid);
  const total = totalOf(p);
  const hidden = faceDown(p);
  const offline = !info.isBot && !info.connected;
  const label = `${info.name}${info.isBot && info.level ? `, ${LEVEL_LABEL[info.level]} computer` : ''}. Total ${fmt(total)}${
    hidden && p.grid.length ? `, ${hidden} cards hidden` : ''
  }${pairs.size ? `, ${pairs.size} matched column${pairs.size > 1 ? 's' : ''}` : ''}.${active ? ' Their turn.' : ''}${out ? ' Went out.' : ''}${extras.lastShot === 'owed' ? ' Owes a last shot.' : ''}${offline ? ' Offline.' : ''}${
    info.autopilot ? ' The computer is playing for them.' : ''
  }`;
  const ringMs = extras.deadline ? Math.max(0, extras.deadline - Date.now()) : 0;
  const medalSize = medal ?? (compact ? 22 : 28);
  const flagSize = medal ? 12 : compact ? 13 : 15;

  return (
    <div
      className={`tile${strip ? ' strip' : ''}${active ? ' active' : ''}${compact ? ' compact' : ''}${sittingOut ? ' out-of-hole' : ''}${offline || info.autopilot ? ' away' : ''}`}
      role="group"
      aria-label={label}
      data-seat={seat}
      {...(strip ? { onClick: showTip } : {})}
    >
      <span className="tile-head" aria-hidden="true">
        <span className="tile-medal">
          <PlayerMedallion seat={seat} size={medalSize} active={active} />
          {extras.deadline !== null && ringMs > 0 && (
            <svg className="drain-ring" viewBox="0 0 36 36" key={extras.deadline}>
              <circle
                cx="18"
                cy="18"
                r="16"
                style={{ animationDuration: `${ringMs}ms`, '--start': Math.min(1, ringMs / IDLE_TURN_MS) } as CSSProperties}
              />
            </svg>
          )}
          {extras.clock !== null && extras.clock <= 10 ? (
            <span className="tile-badge clock num">0:{String(extras.clock).padStart(2, '0')}</span>
          ) : teeing ? (
            <span className="tile-badge">
              <span className="spinner small tile-spin" />
            </span>
          ) : out ? (
            <span className="tile-badge flag">
              <Flag size={flagSize} />
            </span>
          ) : extras.lastShot === 'owed' ? (
            <span className="tile-badge flag">
              <Flag size={flagSize} outline />
            </span>
          ) : offline ? (
            <span className="tile-badge">
              <IconOffline size={12} />
            </span>
          ) : null}
        </span>
        {!strip && <span className="tile-name">{tileName(info.name, narrow)}</span>}
        <span className="tile-total num">{fmt(total)}</span>
      </span>
      <span className="tile-grid" aria-hidden="true">
        {p.grid.length === 8
          ? [0, 1, 2, 3].map((c) => {
              const paired = pairs.has(c);
              const m5 = paired && p.grid[c]?.card?.value === -5;
              const formDelay = extras.forming?.get(c);
              return (
                <span
                  key={c}
                  className={`tile-col${paired ? ' paired' : ''}${m5 ? ' m5' : ''}${paired && formDelay !== undefined ? ' forming' : ''}`}
                  style={formDelay !== undefined ? ({ '--form-delay': `${formDelay}ms` } as CSSProperties) : undefined}
                >
                  {[c, c + 4].map((i) => {
                    const s = p.grid[i]!;
                    return (
                      <MiniCell
                        key={i}
                        value={s.card?.value ?? null}
                        faceUp={s.faceUp}
                        {...(s.card && s.faceUp ? { flightId: s.card.id } : {})}
                        className={extras.flash.has(i) ? 'flash' : ''}
                        slotKey={`${seat}:${i}`}
                      />
                    );
                  })}
                </span>
              );
            })
          : null}
      </span>
      {extras.sticker && <Sticker key={extras.sticker.id} emote={extras.sticker.emote} />}
      {strip && tip && (
        <span className="tile-tip mark" role="status">
          {info.name}
        </span>
      )}
    </div>
  );
});
